import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getPatchReceptionCallHistoryByTaskSidMutationKey,
  useGetReceptionDevice,
  usePatchReceptionCallHistoryByTaskSid,
  useUpdateTaskStatus,
} from '@reception-web/common/api/www'
import { HISTORY_CALLER_STATUS } from '@reception-web/common/consts'
import { useAudio, useCreateTaskApi } from '@reception-web/common/hooks'
import * as Sentry from '@sentry/react'
import {
  DeviceLabelTriggerStatus,
  useMeetingManager,
} from 'amazon-chime-sdk-component-library-react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button, DevicePermissionModal, Text } from '../../components'
import { ERROR_TYPE, PATH, TIMEOUT_MILLISECONDS } from '../../constants'
import {
  useAttendeePresenceListener,
  useContact,
  useDeviceIdProvider,
  useMeeting,
  useMeetingContext,
  useTaskMeetingApi,
} from '../../hooks'

const DELAYED_MESSAGE_TIMEOUT_MILLISECONDS = 1000 * 10 // 10 seconds

export function DialFrame() {
  const { getRoute, deviceId } = useDeviceIdProvider()
  const { data: receptionDevice } = useGetReceptionDevice(deviceId ?? '')
  const { requirement, language } = useContact()
  const {
    trigger: createTask,
    data: task,
    error,
  } = useCreateTaskApi(deviceId, {
    deviceId: deviceId ?? '',
    language,
    requirement,
  })

  const taskRef = useRef<{ task_sid?: string } | undefined>(undefined)
  const { trigger: updateTaskStatus, isMutating: isUpdatingTaskStatus } =
    useUpdateTaskStatus(deviceId ?? '')
  const { trigger: updateHistory, isMutating: isUpdatingHistory } =
    usePatchReceptionCallHistoryByTaskSid(
      deviceId ?? '',
      task?.task_sid ?? '',
      {
        swr: {
          swrKey: getPatchReceptionCallHistoryByTaskSidMutationKey(
            deviceId ?? '',
            task?.task_sid ?? ''
          )[0],
        },
      }
    )
  const { data: meetingData } = useTaskMeetingApi(task?.task_sid)
  const { attendee, setMeeting, setAttendee } = useMeetingContext()
  const location = useLocation()
  const executedInit = useRef(false)
  const errorTimer = useRef<NodeJS.Timeout | null>(null)
  const delayedMessageTimer = useRef<NodeJS.Timeout | null>(null)
  const [showDelayedMessage, setShowDelayedMessage] = useState(false)
  const navigate = useNavigate()
  const { play, stop } = useAudio({ source: '/audio/ring.mp3', repeat: true })
  const { play: playFailed } = useAudio({
    source: '/audio/failed.mp3',
    repeat: false,
  })
  const { i18n } = useTranslation()
  const ja = i18n.getFixedT('ja', 'translation', 'frames.DialFrame')
  const en = i18n.getFixedT('en', 'translation', 'frames.DialFrame')

  const { startMeeting, deviceLabelTriggerStatus } = useMeeting()
  const meetingManager = useMeetingManager()
  const [canceled, setCanceled] = useState(false)

  useEffect(() => {
    if (error) {
      Sentry.captureException(
        new Error(
          `Failed to create task at ${receptionDevice?.data?.property_translations?.en?.name}.
現在suitebook callsに障害が発生し、通話ができません。
ゲストには、施設の緊急連絡先を表示・案内しています。
SQAメンバーは、電話での問い合わせ対応をお願いします。
有人施設は、フロントでのチェックイン対応・問い合わせ対応をお願いします。
障害が解消次第、改めてご連絡いたします。
ご迷惑おかけしますが、宜しくお願いします。`
        ),
        {
          tags: { alert: 'critical' },
        }
      )
      navigate(getRoute(PATH.maintenance), {
        state: {
          error: ERROR_TYPE.FAILED_TO_DIALED,
        },
      })
    }
  }, [error, getRoute, navigate, receptionDevice])

  useAttendeePresenceListener({
    selfId: attendee?.AttendeeId ?? '',
    onJoin: () => {
      navigate(getRoute(PATH.about))
    },
  })

  useEffect(() => {
    if (meetingData?.Meeting && meetingData.Attendees) {
      setMeeting(meetingData.Meeting)
      setAttendee(meetingData.Attendees[0])
      startMeeting()
      meetingManager.stopVideoInputDevice()
    }
  }, [meetingData, setMeeting, setAttendee, startMeeting, meetingManager])

  useEffect(() => {
    if (task?.task_sid) {
      taskRef.current = task
    }
  }, [task])

  const cancelFunction = useCallback(
    async (
      cause: 'userAction' | 'timeout' | 'devicePermission' = 'userAction'
    ) => {
      stop()
      playFailed()
      setCanceled(true)
      try {
        // 404 error occurs when attempting to cancel a call that has been assigned to a crew
        await updateTaskStatus({
          status: 'canceled',
          task_sid: taskRef.current?.task_sid,
        })

        const time = new Date().toISOString()
        await updateHistory(
          cause === 'timeout'
            ? {
                timeouted: time,
                caller_status: HISTORY_CALLER_STATUS.TIMEOUT,
              }
            : {
                canceled: time,
                caller_status: HISTORY_CALLER_STATUS.CANCEL,
              }
        )
      } finally {
        if (cause === 'timeout' || cause === 'userAction') {
          navigate({
            pathname: getRoute(PATH.cancel),
            search: `?isTimeout=${cause === 'timeout'}`,
          })
        }
      }
    },
    [stop, playFailed, updateTaskStatus, updateHistory, getRoute, navigate]
  )

  useEffect(() => {
    if (
      deviceLabelTriggerStatus === DeviceLabelTriggerStatus.DENIED &&
      !canceled
    ) {
      cancelFunction('devicePermission')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceLabelTriggerStatus])

  const clearTimers = useCallback(() => {
    if (errorTimer.current) {
      clearTimeout(errorTimer.current)
    }
    if (delayedMessageTimer.current) {
      clearTimeout(delayedMessageTimer.current)
    }
  }, [])

  useEffect(() => {
    async function init() {
      // Avoid calling createMeeting multiple times in development mode
      if (import.meta.env.MODE.match(/development/) && executedInit.current) {
        return
      }

      if (!executedInit.current) {
        executedInit.current = true
        await createTask()
        play()
        errorTimer.current = setTimeout(() => {
          cancelFunction('timeout')
        }, TIMEOUT_MILLISECONDS)
        delayedMessageTimer.current = setTimeout(() => {
          setShowDelayedMessage(true)
        }, DELAYED_MESSAGE_TIMEOUT_MILLISECONDS)
      }
    }

    init()
  }, [createTask, cancelFunction, play])

  useEffect(
    () => () => {
      stop()
      clearTimers()
      setShowDelayedMessage(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  )

  return (
    <>
      <div className="flex h-screen w-full flex-col items-center justify-center gap-5 bg-off-white p-[60px]">
        <Text size="heading2" className="flex">
          <Trans
            i18nKey={
              showDelayedMessage
                ? 'frames.DialFrame.waitingDelayed'
                : 'frames.DialFrame.waiting'
            }
            tOptions={{ lng: 'ja' }}
          />
          <br />
          <Trans
            i18nKey={
              showDelayedMessage
                ? 'frames.DialFrame.waitingDelayed'
                : 'frames.DialFrame.waiting'
            }
            tOptions={{ lng: 'en' }}
          />
        </Text>
        <AiOutlineLoading3Quarters size={40} className="animate-spin" />
        {taskRef.current?.task_sid && (
          <Button
            fill
            className="my-5 h-[70px] w-[250px]"
            onClick={() => {
              cancelFunction('userAction')
              if (errorTimer.current) {
                clearTimeout(errorTimer.current)
              }
            }}
            disabled={isUpdatingTaskStatus || isUpdatingHistory}
          >
            <Text size="lg2" weight="normal" align="center">
              {isUpdatingTaskStatus || isUpdatingHistory ? (
                <AiOutlineLoading3Quarters size={40} className="animate-spin" />
              ) : (
                <>
                  {ja('cancel')}
                  <br />
                  {en('cancel')}
                </>
              )}
            </Text>
          </Button>
        )}
      </div>
      {deviceLabelTriggerStatus === DeviceLabelTriggerStatus.DENIED && (
        <DevicePermissionModal />
      )}
    </>
  )
}
